export async function renameAiChat(chatId: string, rawTitle: string): Promise<string> {
  const title = rawTitle.trim();
  if (!title) throw new Error('Der Titel darf nicht leer sein.');
  const response = await fetch(`/api/ai/chats/${encodeURIComponent(chatId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ title }),
  });
  if (!response.ok) {
    let message = 'Titel konnte nicht gespeichert werden.';
    try {
      const body = await response.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep default */
    }
    throw new Error(message);
  }
  const body = await response.json() as { title?: string };
  return String(body.title ?? title).trim() || title;
}

export async function deleteAiChat(chatId: string): Promise<void> {
  const response = await fetch(`/api/ai/chats/${encodeURIComponent(chatId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Chat konnte nicht gelöscht werden.';
    try {
      const body = await response.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep default */
    }
    throw new Error(message);
  }
}

export const AI_CHAT_PIN_LIMIT_MESSAGE = 'Maximal 20 Chats können angeheftet werden.';

export async function pinAiChat(chatId: string, pinned: boolean): Promise<string | null> {
  const response = await fetch(`/api/ai/chats/${encodeURIComponent(chatId)}/pin`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ pinned }),
  });
  if (!response.ok) {
    let message = 'Anheften konnte nicht gespeichert werden.';
    if (response.status === 409) message = AI_CHAT_PIN_LIMIT_MESSAGE;
    else {
      try {
        const body = await response.json() as { error?: string };
        if (body.error) message = body.error;
      } catch {
        /* keep default */
      }
    }
    throw new Error(message);
  }
  const body = await response.json() as { pinned_at?: string | null };
  return body.pinned_at ?? null;
}

export type UndoLastWriteResponse = {
  alreadyUndone: boolean;
  undone: Array<{ tool: string; label: string }>;
  skipped: Array<{ tool: string; label: string; reason?: string }>;
  note: string | null;
  noteRow: {
    id: string;
    role: "assistant";
    content: string;
    parts: unknown[];
    request_id: string;
  } | null;
  updatedMessage: { id: string; parts: unknown[] } | null;
};

export async function setActiveAiChatLeaf(chatId: string, messageId: string): Promise<string> {
  const response = await fetch(`/api/ai/chats/${encodeURIComponent(chatId)}/active-leaf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ messageId }),
  });
  if (!response.ok) {
    let message = "Zweig konnte nicht gewechselt werden.";
    try {
      const body = await response.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep default */
    }
    throw new Error(message);
  }
  const body = await response.json() as { active_leaf_id?: string };
  return String(body.active_leaf_id ?? messageId);
}

export async function undoLastAiWriteTurn(chatId: string): Promise<UndoLastWriteResponse> {
  const response = await fetch(`/api/ai/chats/${encodeURIComponent(chatId)}/undo`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    let message = "Rückgängig machen ist fehlgeschlagen.";
    try {
      const body = await response.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep default */
    }
    throw new Error(message);
  }
  return await response.json() as UndoLastWriteResponse;
}


export type AiChatFeedbackRating = 'up' | 'down';

/** Daumen hoch/runter für eine Antwort. rating null entfernt die Bewertung. */
export async function sendAiChatFeedback(
  chatId: string,
  messageId: string,
  requestId: string,
  rating: AiChatFeedbackRating | null,
): Promise<void> {
  const response = await fetch(
    `/api/ai/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ rating, requestId }),
    },
  );
  if (!response.ok) throw new Error('Bewertung konnte nicht gespeichert werden.');
}
